import type { ProviderDetail } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CalendarTab } from './CalendarTab';
import { CalendarViewerTab } from './CalendarViewerTab';
import { CredentialingTab } from './CredentialingTab';
import { ExternalCalendarTab } from './ExternalCalendarTab';
import { LicensureTab } from './LicensureTab';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { ProviderAvatar } from '@/components/provider-avatar';
import { DetailPageSkeleton, ErrorBanner } from '@/components/ui/state-views';
import { getProvider } from '@/config/api-helpers';
import { recordVisit } from '@/hooks/useVisitHistory';
import { formatDate } from '@/utils/format';

const FIELDS: { label: string; render: (p: ProviderDetail) => string }[] = [
  { label: 'Provider ID', render: (p) => String(p.providerId) },
  { label: 'User ID', render: (p) => String(p.userId) },
  { label: 'Type', render: (p) => p.type },
  { label: 'Status', render: (p) => (p.isActive ? 'Active' : 'Inactive') },
  { label: 'Tier', render: (p) => (p.tag != null ? String(p.tag) : '\u2014') },
  { label: 'State', render: (p) => p.state ?? '\u2014' },
  { label: 'Start Date', render: (p) => formatDate(p.startDate) },
  { label: 'Phone', render: (p) => p.phone ?? '\u2014' },
  { label: 'Healthie ID', render: (p) => p.healthieId ?? '\u2014' },
  { label: 'NPI', render: (p) => p.npi ?? '\u2014' },
  { label: 'City', render: (p) => p.city ?? '\u2014' },
  { label: 'Education', render: (p) => p.education ?? '\u2014' },
];

const TABS = ['Calendar', 'Calendar Settings', 'External Calendar', 'Licensure', 'Credentialing'] as const;
type Tab = (typeof TABS)[number];

function TabContent({ tab, providerId }: { tab: Tab; providerId: number }) {
  switch (tab) {
    case 'Calendar':
      return <CalendarViewerTab providerId={providerId} />;
    case 'Calendar Settings':
      return <CalendarTab providerId={providerId} />;
    case 'External Calendar':
      return <ExternalCalendarTab providerId={providerId} />;
    case 'Licensure':
      return <LicensureTab providerId={providerId} />;
    case 'Credentialing':
      return <CredentialingTab providerId={providerId} />;
  }
}

export function ProviderPage() {
  const { providerId } = useParams();
  const {
    data: provider,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['provider', providerId],
    queryFn: () => getProvider(providerId!),
    enabled: providerId != null,
  });
  const [activeTab, setActiveTab] = useState<Tab>('Calendar');

  const displayName = provider ? `${provider.firstName} ${provider.lastName}` : 'Provider';

  useEffect(() => {
    if (provider) {
      recordVisit(`/providers/${provider.providerId}`, displayName, 'provider');
    }
  }, [provider, displayName]);

  return (
    <div className='space-y-6'>
      <Breadcrumbs items={[{ label: 'Providers', to: '/providers' }, { label: displayName }]} />

      {error ? (
        <ErrorBanner
          message={error?.message ?? 'Failed to load provider'}
          onRetry={() => refetch()}
          title='Provider is unavailable'
        />
      ) : isLoading ? (
        <DetailPageSkeleton fields={FIELDS.length} label='Loading provider' showAvatar tabs={TABS.length} />
      ) : provider ? (
        <div className='space-y-6'>
          <div className='rounded-lg border border-neutral-200 bg-white p-6'>
            <div className='flex items-start gap-4'>
              <ProviderAvatar
                firstName={provider.firstName}
                lastName={provider.lastName}
                photoUrl={provider.photoUrl}
                size='lg'
              />
              <div className='min-w-0 flex-1'>
                <h2 className='font-mediumbold mb-1 text-2xl text-neutral-900'>{displayName}</h2>
                <p className='mb-4 text-sm text-neutral-500'>{provider.email}</p>
                <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
                  {FIELDS.map((f) => (
                    <div key={f.label}>
                      <span className='text-neutral-500'>{f.label}:</span> {f.render(provider)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className='overflow-hidden rounded-lg border border-neutral-200 bg-white'>
            <div className='flex border-b border-neutral-200'>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'border-b-2 border-neutral-900 text-neutral-900'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                  onClick={() => setActiveTab(tab)}
                  type='button'
                >
                  {tab}
                </button>
              ))}
            </div>
            <TabContent tab={activeTab} providerId={provider.providerId} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
